import { PartialDeep } from '@juln/type-fest';
import { noop } from 'lodash';
import Pubsub from 'pubsub-js';
import React, { useEffect, useRef, useState } from 'react';
import type { LiteralUnion } from 'type-fest';
import type { IMeituAccount } from '@meitu/account-web';
import { TypeOpenVipTeamModal } from '../teamInfo/identitySwitch';
import { AppTheme } from '../types/global';
import { LangType } from '../types/locale';

const ConfigUpdateEvent = 'xdesign-widgets__config-update-event';

export enum ENV_ENUM {
    dev = 'dev',
    pre = 'pre',
    beta = 'beta',
    release = 'release',
}

export interface Config {
    theme: AppTheme;
    env: ENV_ENUM;
    client_id: string;
    gid: string;
    device_id: string;
    channel: string;
    /**
     * 国家编码
     */
    country_code: string;
    /**
     * 埋点用的app_key
     */
    app_key: string;

    module_id: string;

    /**
     * 业务登录方式后缓存的token对应的key名称，默认是access_token
     */
    silent_token_key: string;
    /**
     * 多语言, 可以透传至服务端
     */
    lang: LangType;
    /**
     * 区分海外环境标识，为true的话为xd或者dk全球化版本
     */
    isIntl: boolean;
    /**
     * 是否启用团队版功能
     */
    isTeam: boolean;
    /**
     * 应用名称
     */
    appName: 'x-design' | 'designkit';
    /**
     * 是否抠图全球化站点
     */
    isCutoutGlobal?: boolean;
    /** ColorGroup组件的通用配置 */
    colorGroupCommonConfig: {
        /**
         * @deprecated
         * 已废弃, 使用hiddenBrandFn替代
         * 隐藏团队版品牌色卡选择器 (不是团队版账号时, 强制隐藏, 传参无效)
         */
        hiddenBrand?: boolean;
        /** 品牌列表排序规则, 默认为 'id,asc' */
        brandOrderBy?: LiteralUnion<'id,asc' | 'id,desc', string>;
        /**
         * 默认逻辑: 团队身份显示品牌色, 非团队身份隐藏品牌色
         */
        hiddenBrandFn?: (params: { isTeam: boolean }) => boolean;
        /** 打开订阅弹窗 */
        openVipModal?: TypeOpenVipTeamModal;
        /** 如果没有传onToggleBizTab属性的话, 执行这个函数 (主要用来校验是否给ColorGroup的品牌库埋点的, 如果没有, 弹报错, 不然每次搞新功能都会漏接) */
        ifNoOnToggleBizTabProp?: () => void;
    };
    /** 登录身份切换弹窗组件的通用配置 */
    loginIdentitySwitchModalConfig: {
        /** 有保存功能的编辑器都需要加这个提示 */
        showEditorSaveTip: boolean;
    };
    meituAccountInstance: IMeituAccount | null;
    userInfo: {
        screen_name: string;
        id: number;
        education_role?: number;
    };
    /** @deprecated 使用setUploadConfig替代 */
    uploaderAppProp?: string;
    /** wps 用户 */
    isWpsUser?: boolean;
    /** wps 登录授权成功后的回调路径，如 'puzzle'，最终拼接为 https://{envPrefix}.designkit.cn/puzzle */
    wpsRedirectPath?: string;
    /**
     * 错误上报函数，由宿主应用注入（如 Sentry.captureException）。
     * 共享包内部不直接依赖具体的监控 SDK，统一通过此函数上报。
     */
    reportError?: (error: unknown, extra?: Record<string, unknown>) => void;
    /**
     * 未登录时拉起登录并等待结果；登录成功返回 true，取消返回 false。
     * 由宿主注入（如 isNeedToLogin），供模特生成等需登录的流程 await。
     */
    ensureLogin?: () => Promise<boolean>;
}

/**
 * 获取country_code 兜底
 * @param language  通过navigator.language获取的值
 * @returns
 */
const getCountryCode = () => {
    // 如果是在 SSR 环境下，直接返回空字符串
    if (typeof window === 'undefined') {
        return '';
    }

    const language = navigator.language;
    const regionMap: { [key: string]: string } = {
        'zh-CN': 'CN',
        'zh-TW': 'TW',
        'ja-JP': 'JP',
        'ko-KR': 'KR',
        'en-US': 'US',
        'en-AU': 'AU',
        'en-CA': 'CA',
        'zh-HK': 'HK',
        'th-TH': 'TH',
        'en-GB': 'GB',
        'en-SG': 'SG',
        'ms-SG': 'SG',
        'zh-SG': 'SG',
        'vi-VN': 'VN',
        'zh-MO': 'MO',
        'pt-MO': 'MO',
        'tl-PH': 'PH',
        'en-PH': 'PH',
        'id-ID': 'ID',
        'en-NZ': 'NZ',
        'mi-NZ': 'NZ',
        fil: 'PH',
    };

    if (regionMap[language]) {
        return regionMap[language];
    }
    // 如果有区域代码，格式是 <language>-<region>，
    // 例如：en-US — 英语（美国）、zh-CN — 简体中文（中国）
    if (language && language.includes('-')) {
        const [_, rg] = language.split('-');
        if (rg) {
            return rg;
        }
    }
    // 用语言匹配
    const keys = Object.keys(regionMap);
    for (let i = 0; i < keys.length; i++) {
        const element = keys[i];
        if (element.includes(language)) {
            return regionMap[element];
        }
    }

    return language;
};

const _configState: Config = {
    theme: 'light',
    env: ENV_ENUM.release,
    client_id: '',
    gid: '',
    device_id: '',
    channel: '',
    /**
     * 外部传入的 country_code 都采用 utils 包异步设置的方式，所以会有一个“空窗期”
     * 这里兜底一个默认值，默认是取 navigator.language 进行判断
     */
    country_code: getCountryCode(),
    app_key: '',
    module_id: '',
    silent_token_key: 'access_token',
    lang: 'zh-Hans',
    isIntl: false,
    isTeam: false,
    isCutoutGlobal: false,
    appName: 'x-design',
    colorGroupCommonConfig: {
        openVipModal: noop,
        ifNoOnToggleBizTabProp: noop,
    },
    loginIdentitySwitchModalConfig: {
        /** 编辑器都需要加这个提示 */
        showEditorSaveTip: false,
    },
    meituAccountInstance: null,
    userInfo: {
        screen_name: '',
        id: 0,
    },
    isWpsUser: false,
    wpsRedirectPath: undefined,
    reportError: undefined,
    ensureLogin: undefined,
};

export const getConfig = (): Config => ({
    ..._configState,
});

export const setConfig = <K extends keyof Config>(key: K, value: Config[K]) => {
    _configState[key] = value;
    if (key === 'env' && value === ENV_ENUM.dev) {
        _configState['env'] = ENV_ENUM.pre;
    }
    Pubsub.publish(ConfigUpdateEvent);
};

export const setConfigPartial = (params: PartialDeep<Config>) => {
    for (const key in params) {
        if (Object.prototype.hasOwnProperty.call(params, key)) {
            // @ts-expect-error TS suppress
            setConfig(key, params[key]);
        }
    }
};

export const useConfig = () => {
    const [ctx, setCtx] = useState(_configState);
    useEffect(() => {
        const onSub = () => {
            setCtx({ ..._configState });
        };
        Pubsub.subscribe(ConfigUpdateEvent, onSub);
        return () => {
            Pubsub.unsubscribe(onSub);
        };
    }, []);
    return ctx;
};

const ConfigProvider = ({ value, children }: { value: Partial<Config>; children?: React.ReactNode }) => {
    const isFirst = useRef<boolean>(true);

    if (isFirst.current) {
        setConfigPartial(value);
        isFirst.current = false;
    }

    useEffect(() => {
        setConfigPartial(value);
    }, [value]);
    return <>{children}</>;
};

export default ConfigProvider;
